/**
 * Repositorio Semilla de Documentación PÚBLICA de VeezDelivery
 * Coincidencia 1:1 con la estructura y anclajes oficiales de https://docs.veez.cl/
 */
export const SEED_DOCUMENTATION = [
  // --- DESARROLLADORES ---
  // Grupo: INTRODUCCIÓN
  {
    id: "overview",
    titulo: "Visión General",
    categoria: "desarrolladores",
    orden: 1,
    contenidoMarkdown: `## 🌐 Visión General

La API REST de **VeezDelivery** permite conectar tiendas e-commerce, sistemas ERP y aplicaciones móviles para cotizar, solicitar y monitorear envíos urbanos automatizados.

Todas las solicitudes deben realizarse a través de HTTPS. Las respuestas se entregan estrictamente en formato JSON utilizando la codificación UTF-8.

> **Estructura Estándar de Respuesta:**  
> Todas las respuestas exitosas devuelven \`"success": true\`. En caso de error, devuelven un código HTTP acorde (400, 401, 500) junto con un objeto descriptivo en el campo \`"error"\`.`
  },
  {
    id: "authentication",
    titulo: "Autenticación & Keys",
    categoria: "desarrolladores",
    orden: 2,
    contenidoMarkdown: `## 🔑 Autenticación & API Keys

La API utiliza llaves de autenticación (API Keys) basadas en Bearer Tokens para validar el acceso.

Debes enviar tu API Key en cada solicitud mediante el encabezado HTTP \`Authorization\` o \`X-API-Key\`:

\`\`\`http
Authorization: Bearer vz_live_a1b2c3d4e5f6...
X-API-Key: vz_live_a1b2c3d4e5f6...
\`\`\`

### Modos de Entorno (Producción vs Sandbox)

| Entorno | Prefijo de API Key | Encabezado Opcional | Comportamiento |
| :--- | :--- | :--- | :--- |
| **Producción** | vz_live_... | - | Crea despachos reales con cobro a la cuenta del comercio. |
| **Sandbox (Pruebas)** | vz_test_... | X-Test-Mode: true | Simula rutas y conductores sin generar cobros reales. |`
  },
  {
    id: "prep-time",
    titulo: "Tiempo de Preparación",
    categoria: "desarrolladores",
    orden: 3,
    contenidoMarkdown: `## ⏱️ Tiempo de Preparación (prep_time_mins)

Permite a los comercios indicar cuántos minutos requiere la cocina o el almacén para empaquetar el pedido antes del arribo del repartidor.

El sistema de VeezDelivery utiliza \`prep_time_mins\` para recalcular automáticamente las ventanas de recolección y entrega (ETAs):

- **estimated_pickup_time**: Hora actual + prep_time_mins.
- **estimated_delivery_time**: Hora actual + prep_time_mins + tiempo_de_trayecto_gps.

### Jerarquía de Prioridad:
1. Valor especificado en el payload JSON de la solicitud (\`prep_time_mins\`).
2. Configuración predeterminada del comercio guardada en su perfil (\`/merchants/settings\`).
3. Valor por defecto: 0 minutos (Despacho inmediato).`
  },
  {
    id: "errors",
    titulo: "Códigos de Error",
    categoria: "desarrolladores",
    orden: 4,
    contenidoMarkdown: `## ⚠️ Manejo de Errores & Códigos HTTP

Respuestas estándar en caso de fallo, autenticación denegada o parámetros inválidos.

| Código HTTP | Mensaje de Error | Causa & Solución |
| :--- | :--- | :--- |
| **400 Bad Request** | INVALID_PREP_TIME | El parámetro \`prep_time_mins\` debe ser un entero mayor o igual a 0. |
| **401 Unauthorized** | UNAUTHORIZED | API Key no provista o inválida. Revisa el encabezado Authorization: Bearer. |
| **422 Unprocessable** | OUT_OF_COVERAGE | La dirección de destino se encuentra fuera de la zona de cobertura urbana. |
| **500 Server Error** | INTERNAL_ERROR | Error interno del servidor. Contacta al soporte técnico si persiste. |`
  },

  // Grupo: GUÍAS DE INSTALACIÓN
  {
    id: "install-woocommerce",
    titulo: "WordPress / WooCommerce",
    categoria: "desarrolladores",
    orden: 5,
    contenidoMarkdown: `## 🛒 Guía de Instalación e Integración: WordPress / WooCommerce

El plugin oficial **VeezDelivery Shipping for WooCommerce** (\`veezdelivery-shipping\`) permite el cálculo de tarifas en tiempo real en el checkout, simulación aislada en Sandbox, inyección de campo RUT con validación Módulo 11, despacho automatizado de repartidores y sincronización bidireccional de estados mediante Webhooks y Cron.

### 1. Instalación del Plugin
1. Instala directamente desde el repositorio oficial: [Ver en WordPress.org 🔗](https://wordpress.org/plugins/veezdelivery-shipping-for-woocommerce/) o descárgalo desde la sección de Integraciones en tu panel.
2. En el panel de WordPress, dirígete a **Plugins** > **Añadir Nuevo**.
3. Puedes buscar "VeezDelivery" e instalarlo con un clic, o hacer clic en **Subir Plugin**, seleccionar \`veezdelivery-shipping-for-woocommerce.zip\` y presionar **Instalar Ahora**.
4. Haz clic en **Activar Plugin**.

### 2. Configuración Global de Credenciales (Pestaña Integración)
Ve a **WooCommerce** > **Ajustes** > **Integración** > **VeezDelivery**:

- **Activar/Desactivar**: Marca para habilitar el método de envío VeezDelivery en la tienda.
- **Modo Pruebas / Sandbox**: Habilita la simulación aislada (NO solicita choferes reales ni genera cobros).
- **API Key Producción (Live)** (\`api_key\`): Clave de producción obtenida en tu panel (empieza con \`vz_live_...\`).
- **Clave Secreta (API Secret)** (\`api_secret\`): Clave secreta para autenticación criptográfica de webhooks (empieza con \`vz_sec_...\`).
- **API Key Sandbox (Test)** (\`test_api_key\`): Clave de pruebas (\`vz_test_...\`), utilizada automáticamente cuando el Modo Test está activo.
- **Estado para Despacho** (\`trigger_status\`): Estado del pedido en WooCommerce que gatilla automáticamente la solicitud de chofer (por defecto: \`Procesando\` / \`wc-processing\`).
- **Sincronización de Estado** (\`auto_complete_order\`): Cambia el pedido automáticamente al estado personalizado **Entregado** (\`wc-delivered\`) al completarse la entrega.
- **Notificación al Admin** (\`notify_admin_on_delivery\`): Envía un correo de notificación al administrador cuando se entregue el pedido.

### 3. Configuración del Método de Envío (Zonas de Envío)
Para activar la cotización en tiempo real en el checkout:
1. Dirígete a **WooCommerce** > **Ajustes** > **Envío** > **Zonas de envío**.
2. Añade o edita una Zona de Envío y haz clic en **Añadir método de envío**.
3. Selecciona **VeezDelivery Express Shipping** y haz clic en **Editar**.
4. Configura las opciones de la zona:
   - **Dirección de Retiro por Defecto** (\`store_as_pickup\`): Utiliza la dirección de la tienda de los ajustes generales de WooCommerce. Si se desactiva, permite definir una **Dirección de Retiro Específica** (\`store_address\`).
   - **Tiempo de Preparación (Minutos)** (\`prep_time_mins\`): Minutos estimados para empaquetado/cocina antes del retiro (ej. \`15\` min).
   - **Comisión o Cargo Extra Fijo (Opcional)** (\`handling_fee\`): Recargo fijo adicionado a la tarifa calculada por manipulación.
   - **Despacho Automático** (\`auto_dispatch\`): Solicita el repartidor automáticamente al pasar el pedido al estado de despacho.

### 4. Campo RUT del Receptor y Validación Módulo 11
El plugin añade automáticamente el campo **RUT / Identificación** (\`billing_rut\`) en los detalles de facturación del checkout:
- **Validación Módulo 11 en Tiempo Real**: Valida el formato chileno (ej: \`12.345.678-5\`) y rechaza dígitos verificadores erróneos antes de procesar el pago.
- **Persistencia**: Guarda el valor en el metadato del pedido (\`_billing_rut\`) y lo muestra en la vista del pedido en el panel de administración.
- **Matching Automático**: Al despachar el pedido a VeezDelivery, el RUT se envía en el campo \`customer_rut\`. Si el receptor es un usuario registrado en la plataforma, el sistema vincula automáticamente el despacho a su cuenta para trazabilidad directa (\`isRegisteredUser: true\`).`
  },
  {
    id: "install-shopify",
    titulo: "Shopify Integration",
    categoria: "desarrolladores",
    orden: 6,
    contenidoMarkdown: `## 🛍️ Guía de Instalación: Shopify Integration

Conecta tu tienda Shopify con VeezDelivery registrando el servicio de cotización de tarifas en checkout (**CarrierService**) y el webhook de despacho automático (**orders/paid**).

### 1. Requisitos Previos
- Obtén tu **Shopify Admin Access Token** (\`shpat_...\`) desde la sección de Apps de tu tienda Shopify.
- Obtén tu **API Key Producción** (\`vz_live_...\`) desde tu panel VeezDelivery.

### 2. Ejecución del Asistente de Configuración
Ejecuta el script oficial de vinculación de Shopify:

\`\`\`bash
node setup-carrier.js \
  --domain=tu-tienda.myshopify.com \
  --token=shpat_xxxxxxxxxxxxxxxx \
  --api-key=vz_live_xxxxxxxxxxxxxxxx
\`\`\`

### 3. Servicios Registrados Automáticamente
- **CarrierService**: Registra el servicio "VeezDelivery Express" llamando a \`https://api.veez.cl/api/v1/shopify/carrier-service\`.
- **Webhook de Despacho Automático**: Registra el evento \`orders/paid\` llamando a \`https://api.veez.cl/api/v1/shopify/webhooks/orders-create\` para solicitar repartidores automáticamente.
- **Extracción de RUT**: Extrae automáticamente el RUT del comprador desde \`note_attributes\` (RUT, DNI, Identificación) o campos de localización de Shopify Chile para identificar al destinatario.`
  },
  {
    id: "install-odoo",
    titulo: "Odoo ERP Addon",
    categoria: "desarrolladores",
    orden: 7,
    contenidoMarkdown: `## 📦 Guía de Instalación: Odoo ERP Addon (v16, v17, v18+)

El conector oficial de Odoo (\`delivery_veezdelivery\`) integra la logística de VeezDelivery con tus Pedidos de Venta (**Sale Orders**) y Ordenes de Entrega (**Stock Picking**).

### 1. Instalación del Módulo
1. Descarga el paquete oficial: [Descargar Addon Odoo (.zip) ⬇️](/downloads/odoo-veezdelivery.zip)
2. Descomprime la carpeta \`delivery_veezdelivery\` dentro del directorio de addons personalizados de tu servidor Odoo (\`custom_addons/\`).
3. Activa el **Modo Desarrollador** en Odoo, ve a **Aplicaciones** > **Actualizar Lista de Aplicaciones** e instala **VeezDelivery Shipping Integration**.

### 2. Configuración del Proveedor de Envíos
Dirígete a **Inventario** > **Configuración** > **Métodos de Envío** y crea/edita el método **VeezDelivery**:

- **Proveedor**: Selecciona \`VeezDelivery\`.
- **Modo Pruebas / Sandbox** (\`veez_test_mode\`): Habilita para simulación de envíos sin costos ni avisos a choferes.
- **API Key Producción (Live)** (\`veez_api_key\`): Clave API que empieza con \`vz_live_...\`.
- **API Key Sandbox (Test)** (\`veez_test_api_key\`): Clave API de pruebas (\`vz_test_...\`).
- **URL Base de la API** (\`veez_api_url\`): URL base (\`https://api.veez.cl/api/v1\`).
- **Dirección de Recogida / Almacén** (\`veez_store_address\`): Dirección física de retiro (si se omite, usará la dirección de la compañía en Odoo).
- **Tipo de Despacho** (\`veez_dispatch_mode\`): Inmediato ("Al tiro" / On-Demand) o Programado.
- **Tiempo de Preparación (Minutos)** (\`veez_prep_time_mins\`): Minutos estimados de empaque antes del retiro (default: \`15\` min).
- **Cargo Adicional Fijo** (\`veez_handling_fee\`): Monto adicional sumado a la tarifa de envío.
- **Despacho Automático** (\`veez_auto_dispatch\`): Emite automáticamente el pedido a la flota al validar el albarán de entrega (**Stock Picking**).
- **Extracción de RUT del Receptor**: Mapea automáticamente el campo \`vat\` del cliente asociado (\`dest_partner.vat\`) a \`customer_rut\` para vincular el despacho con usuarios registrados en VeezDelivery.`
  },
  {
    id: "install-custom",
    titulo: "Integración Personalizada",
    categoria: "desarrolladores",
    orden: 8,
    contenidoMarkdown: `## 💻 Guía de Integración Personalizada

Conecta cualquier backend personalizado (Node.js, Express, Next.js, Python, PHP, Go, C#) usando nuestras peticiones HTTPS REST estándar o SDKs oficiales.

### Flujo de Integración Recomendado:
1. **Cotizar Tarifa**: Envía un \`POST /api/v1/shipping/quote\` con la dirección de entrega y peso.
2. **Crear Despacho**: Envía un \`POST /api/v1/orders\` con los datos del destinatario, su RUT (\`customer_rut\`) y tiempo de preparación (\`prep_time_mins\`).
3. **Tracking en Tiempo Real**: Consulta \`GET /api/v1/orders/:id\` o habilita Webhooks para recibir actualizaciones de estado GPS.`
  },

  // Grupo: SDKS & OPENAPI
  {
    id: "openapi-spec",
    titulo: "OpenAPI 3.1 Spec",
    categoria: "desarrolladores",
    orden: 9,
    contenidoMarkdown: `## 📄 Especificación OpenAPI 3.1.0 (Swagger)

Puedes descargar o consultar el contrato unificado OpenAPI 3.1.0 para explorar los endpoints o generar SDKs automáticos.

[Descargar openapi.yaml (3.1.0) ⬇️](/openapi.yaml)`
  },
  {
    id: "sdk-javascript",
    titulo: "JS/TS Monorepo (@veez)",
    categoria: "desarrolladores",
    orden: 10,
    contenidoMarkdown: `## 🚀 Monorrepo JavaScript / TypeScript (@veez)

Suite modular en TypeScript optimizada para Node.js, Express, Next.js, React y React Native.

#### 1. Instalación
\`\`\`bash
npm install @veez/node @veez/core
\`\`\`

#### 2. Ejemplo de Uso (Node.js / Express / TypeScript):
\`\`\`typescript
import { VeezClient } from '@veez/node';

const veez = new VeezClient({
  apiKey: process.env.VEEZ_API_KEY // vz_live_... o vz_test_...
});

// 1. Cotizar envío
const quote = await veez.getShippingQuote({
  destination: "Av. Providencia 1234, Santiago",
  weight_kg: 1.5,
  prep_time_mins: 15
});
console.log("Tarifa:", quote.price, quote.currency);

// 2. Crear orden de despacho con RUT de destinatario
const order = await veez.createOrder({
  customer: {
    name: "María González",
    phone: "+56912345678"
  },
  customer_rut: "12.345.678-5", // Vincula automáticamente si la usuaria está registrada
  delivery_address: "Av. Providencia 1234, Dpto 502",
  prep_time_mins: 15,
  notes: "Dejar en conserjería"
});
console.log("Pedido creado ID:", order.order_id);

// 3. Obtener tracking en tiempo real
const tracking = await veez.getTrackingDetails(order.order_id);
console.log("Estado:", tracking.status);
\`\`\``
  },
  {
    id: "sdk-php",
    titulo: "PHP SDK (VeezDelivery\\VeezClient)",
    categoria: "desarrolladores",
    orden: 11,
    contenidoMarkdown: `## 🐘 SDK Oficial en PHP (\`VeezDelivery\\VeezClient\`)

Cliente nativo en PHP con cURL optimizado para Laravel, Symfony, WordPress o proyectos PHP puros.

#### 1. Instalación vía Composer
\`\`\`bash
composer require veezdelivery/veez-php-sdk
\`\`\`

#### 2. Ejemplo de Uso:
\`\`\`php
<?php
use VeezDelivery\\VeezClient;

$veez = new VeezClient('vz_live_83f9102xxxxxxxx');

// 1. Cotizar Envío
$quote = $veez->getShippingQuote([
    'destination' => 'Av. Apoquindo 4500, Las Condes',
    'prep_time_mins' => 15
]);
echo "Tarifa: " . $quote['price'] . " " . $quote['currency'];

// 2. Crear Pedido con RUT de Receptor
$order = $veez->createOrder([
    'customer' => [
        'name' => 'Juan Pérez',
        'phone' => '+56912345678'
    ],
    'customer_rut' => '12.345.678-5', // Identificación de receptor
    'delivery_address' => 'Av. Apoquindo 4500',
    'prep_time_mins' => 20
]);

// 3. Rastreo en vivo
$tracking = $veez->getTrackingDetails($order['order_id']);
\`\`\``
  },

  // Grupo: COTIZACIONES & ENVÍOS
  {
    id: "post-shipping-quote",
    titulo: "POST /shipping/quote — Cotizar Envío",
    categoria: "desarrolladores",
    orden: 12,
    contenidoMarkdown: `### POST /shipping/quote — Cotizar Tarifas en Tiempo Real

Calcula las opciones de tarifa, tiempos de viaje y estimaciones de entrega según la distancia y peso.

#### Parámetros del Body (JSON):
| Campo | Tipo | Requerido | Descripción |
| :--- | :--- | :--- | :--- |
| **destination** | string / object | **Requerido** | Dirección completa de entrega o coordenadas {lat, lng}. |
| **origin** | string / object | Opcional | Dirección de origen (si se omite, se usa el perfil de comercio). |
| **prep_time_mins** | integer | Opcional | Minutos de preparación antes de la recolección. |

\`\`\`bash
curl -X POST https://api.veez.cl/api/v1/shipping/quote \\
  -H "Authorization: Bearer vz_live_83f9102" \\
  -H "Content-Type: application/json" \\
  -d '{
    "destination": "Av. Providencia 1234, Santiago",
    "weight_kg": 1.5,
    "prep_time_mins": 15
  }'
\`\`\`

**Respuesta Exitosa (200 OK):**
\`\`\`json
{
  "success": true,
  "quote_id": "quo_88192301",
  "price": 2900,
  "currency": "CLP",
  "prep_time_mins": 15,
  "estimated_pickup_time": "2026-08-21T04:20:00.000Z",
  "estimated_delivery_time": "2026-08-21T04:45:00.000Z"
}
\`\`\``
  },
  {
    id: "post-orders",
    titulo: "POST /orders — Crear Pedido / Despacho",
    categoria: "desarrolladores",
    orden: 13,
    contenidoMarkdown: `### POST /orders — Crear Pedido / Despacho Automático

Solicita la asignación inmediata o programada de un repartidor para recolección y entrega.

#### Parámetros del Body (JSON):
| Campo | Tipo | Requerido | Descripción |
| :--- | :--- | :--- | :--- |
| **customer** | object | **Requerido** | Datos de contacto del cliente (\`name\`, \`phone\`, \`email\`). |
| **customer_rut** | string | Opcional | RUT del receptor (ej: \`12.345.678-5\`). Si coincide con un usuario registrado, se vincula automáticamente el despacho (\`isRegisteredUser: true\`). |
| **delivery_address** | string / object | **Requerido** | Dirección o coordenadas de destino final. |
| **prep_time_mins** | integer | Opcional | Tiempo de preparación antes de recolección (minutos). |
| **notes** | string | Opcional | Instrucciones de entrega o notas al conductor. |

\`\`\`bash
curl -X POST https://api.veez.cl/api/v1/orders \\
  -H "Authorization: Bearer vz_live_83f9102" \\
  -H "Content-Type: application/json" \\
  -d '{
    "customer": {
      "name": "Juan Pérez",
      "phone": "+56912345678"
    },
    "customer_rut": "12.345.678-5",
    "delivery_address": "Av. Apoquindo 4500",
    "prep_time_mins": 20
  }'
\`\`\`

**Respuesta Exitosa (200 OK):**
\`\`\`json
{
  "success": true,
  "order_id": "vz_ord_998123",
  "status": "searching_driver",
  "prep_time_mins": 20,
  "pickup_window": "2026-08-21T04:25:00Z"
}
\`\`\``
  },
  {
    id: "get-orders-id",
    titulo: "GET /orders/:id — Estado del Pedido",
    categoria: "desarrolladores",
    orden: 14,
    contenidoMarkdown: `### GET /orders/:id — Estado del Pedido & Tracking GPS

Obtiene el estado actual del envío, datos del conductor asignado y coordenadas GPS en tiempo real.

\`\`\`bash
curl -X GET https://api.veez.cl/api/v1/orders/vz_ord_998123 \\
  -H "Authorization: Bearer vz_live_83f9102"
\`\`\`

**Respuesta Exitosa (200 OK):**
\`\`\`json
{
  "success": true,
  "order_id": "vz_ord_998123",
  "status": "in_transit",
  "driver": {
    "name": "Carlos Mamani",
    "phone": "+56911223344",
    "vehicle": "Motocicleta Honda Cargo",
    "lat": -33.421,
    "lng": -70.612
  }
}
\`\`\``
  },
  {
    id: "post-orders-cancel",
    titulo: "POST /orders/:id/cancel — Cancelar Pedido",
    categoria: "desarrolladores",
    orden: 15,
    contenidoMarkdown: `### POST /orders/:id/cancel — Cancelar Pedido

Cancela una orden activa antes de que el repartidor recoja el paquete en el local.

\`\`\`bash
curl -X POST https://api.veez.cl/api/v1/orders/vz_ord_998123/cancel \\
  -H "Authorization: Bearer vz_live_83f9102"
\`\`\``
  },

  // Grupo: COMERCIO & CONFIGURACIÓN
  {
    id: "get-merchants-me",
    titulo: "GET /merchants/me — Perfil de Comercio",
    categoria: "desarrolladores",
    orden: 16,
    contenidoMarkdown: `### GET /merchants/me — Perfil de Comercio

Consulta la dirección comercial de origen, tiempo de preparación por defecto y estado de la cuenta.

\`\`\`bash
curl -X GET https://api.veez.cl/api/v1/merchants/me \\
  -H "Authorization: Bearer vz_live_83f9102"
\`\`\``
  },
  {
    id: "post-merchants-settings",
    titulo: "POST/PATCH /merchants/settings — Ajustes del Comercio",
    categoria: "desarrolladores",
    orden: 17,
    contenidoMarkdown: `### POST/PATCH /merchants/settings — Ajustes del Comercio

Actualiza el tiempo de preparación predeterminado (\`prep_time_mins\`), la dirección comercial y las preferencias de notificación.`
  },

  // Grupo: PLATAFORMAS E-COMMERCE
  {
    id: "woocommerce-endpoint",
    titulo: "POST /woocommerce/* — WooCommerce Endpoints",
    categoria: "desarrolladores",
    orden: 18,
    contenidoMarkdown: `### 🛒 Endpoints para WooCommerce (\`/woocommerce/*\`)

Rutas reservadas para el plugin oficial de WooCommerce:

- **\`POST /api/v1/woocommerce/shipping-quote\`**: Cotización de tarifas en checkout considerando peso y dimensiones.
- **\`POST /api/v1/woocommerce/order-webhook\`**: Recepción de webhooks cuando un pedido pasa al estado de despacho (\`trigger_status\` / \`wc-processing\`), transmitiendo el campo \`billing_rut\` como \`customer_rut\`.
- **\`GET /api/v1/woocommerce/order-status\`**: Consulta de estado y tracking para la vista de pedido en WooCommerce.
- **\`POST /wp-json/veezdelivery/v1/order-status\`**: Endpoint REST del plugin WooCommerce para recibir actualizaciones instantáneas de estado (\`delivered\` -> \`wc-delivered\`).`
  },
  {
    id: "shopify-endpoint",
    titulo: "POST /shopify/* — Shopify CarrierService & Webhooks",
    categoria: "desarrolladores",
    orden: 19,
    contenidoMarkdown: `### 🛍️ Endpoints para Shopify (\`/shopify/*\`)

Rutas para la integración con Shopify Admin API:

- **\`POST /api/v1/shopify/carrier-service\`**: Callback de consulta de tarifas en tiempo real durante el checkout de Shopify.
- **\`POST /api/v1/shopify/webhooks/orders-create\`**: Callback receptor con validación HMAC de firma para creación automática de entregas cuando se paga una orden (\`orders/paid\`) con extracción automática de RUT.`
  },
  {
    id: "odoo-endpoint",
    titulo: "POST /odoo/* — Odoo Connector Endpoints",
    categoria: "desarrolladores",
    orden: 20,
    contenidoMarkdown: `### 📦 Endpoints para Odoo ERP (\`/odoo/*\`)

Rutas utilizadas por el addon \`delivery_veezdelivery\` de Odoo:

- **\`POST /api/v1/odoo/rate-shipment\`**: Cálculo de tarifa de envío al agregar el método de envío a un Sale Order.
- **\`POST /api/v1/odoo/send-shipping\`**: Creación automática del despacho al validar la entrega (**Stock Picking**), mapeando el RUT de \`dest_partner.vat\`.
- **\`GET /api/v1/odoo/order-status\`**: Sincronización del estado del repartidor y vehículo asignado.
- **\`POST /api/v1/odoo/cancel-shipment\`**: Cancelación del envío en la flota VeezDelivery.`
  },
  {
    id: "developer-mercadopago",
    titulo: "Pagos con Mercado Pago (API)",
    categoria: "desarrolladores",
    orden: 21,
    contenidoMarkdown: `## 💳 API de Cobros y Tarjetas Guardadas (Mercado Pago)

Veez Delivery expone un conjunto de endpoints REST y Cloud Functions Callables para que los desarrolladores automaticen el registro de tarjetas y el cobro programático de despachos (*Card-on-File*).

---

### 1. Cobro Automático al Crear Despachos (\`POST /orders\`)

Cuando creas una orden de despacho de forma personalizada, puedes indicar el canal de pago a utilizar. Al enviar \`"gateway": "mercadopago"\`, el backend seguro de Veez cobrará el valor dinámico del despacho directamente a la **tarjeta predeterminada** del comercio asociada a Mercado Pago.

#### Ejemplo de Petición cURL:
\`\`\`bash
curl -X POST https://api.veez.cl/api/v1/orders \\
  -H "Authorization: Bearer vz_live_83f9102" \\
  -H "Content-Type: application/json" \\
  -d '{
    "customer": {
      "name": "Ignacio Pérez",
      "phone": "+56998765432"
    },
    "customer_rut": "15.678.901-K",
    "delivery_address": "Av. Vitacura 3500, Vitacura",
    "prep_time_mins": 15,
    "gateway": "mercadopago"
  }'
\`\`\`

---

### 2. Gestión Programática de Tarjetas (SDK / Callables)

Veez proporciona funciones directas en la nube (*HTTPS Callable*) para que tus aplicaciones o portales puedan listar, asociar o eliminar tarjetas sin que tengas que lidiar con la complejidad directa de la API de Mercado Pago.

#### 2.1 Listar Tarjetas del Comercio (\`list_cards\`)
Recupera los detalles de las tarjetas guardadas del usuario, incluyendo tipo de tarjeta, últimos 4 dígitos y fecha de vencimiento.
- **Método**: \`onCall\` / HTTPS POST (\`https://us-central1-veezdelivery.cloudfunctions.net/mercadopago-list_cards\`)
- **Respuesta de éxito**:
  \`\`\`json
  {
    "success": true,
    "cards": [
      {
        "id": "card_982138910",
        "customer_id": "cust_12345678",
        "last_four_digits": "4321",
        "payment_method": {
          "id": "visa",
          "name": "Visa",
          "payment_type_id": "credit_card"
        },
        "expiration_month": 12,
        "expiration_year": 2029,
        "is_default": true
      }
    ]
  }
  \`\`\`

#### 2.2 Registrar Nueva Tarjeta Cifrada (\`save_card\`)
Guarda un token de tarjeta generado previamente mediante el SDK seguro de Mercado Pago en el navegador o app móvil.
- **Método**: \`onCall\` / HTTPS POST (\`https://us-central1-veezdelivery.cloudfunctions.net/mercadopago-save_card\`)
- **Parámetros de Entrada**:
  \`\`\`json
  {
    "token": "mp_token_c923891u0a02390s8d9fsdfh"
  }
  \`\`\`

#### 2.3 Definir Tarjeta Favorita (\`set_default_card\`)
Establece qué tarjeta del perfil de cliente se utilizará para los cobros programáticos de despachos automáticos.
- **Método**: \`onCall\` / HTTPS POST (\`https://us-central1-veezdelivery.cloudfunctions.net/mercadopago-set_default_card\`)
- **Parámetros de Entrada**:
  \`\`\`json
  {
    "cardId": "card_982138910"
  }
  \`\`\`

---

### 3. Petición Directa de Pago (\`create_payment\`)

Si necesitas procesar un cobro específico o manual, puedes interactuar directamente con nuestro servicio de orquestación de pagos.

- **Endpoint REST**: \`POST /api/v1/mercadopago/payment\`
- **Cabecera de Idempotencia**: \`X-Idempotency-Key: <UUID_UNICO>\` *(Obligatorio para prevenir cobros duplicados en caso de inestabilidad de red)*.
- **Payload**:
  \`\`\`json
  {
    "order_id": "vz_ord_1002341",
    "amount": 4990,
    "description": "Despacho Express Veez - Orden #1002341",
    "payer": {
      "email": "comercio@ejemplo.cl"
    }
  }
  \`\`\`

---

### 4. Notificación y Webhooks de Mercado Pago

Nuestra API Gateway escucha en tiempo real las notificaciones instantáneas de pago (IPN / Webhooks) oficiales enviados por los servidores de Mercado Pago en el endpoint:
- **Webhook URL**: \`POST https://api.veez.cl/api/v1/mercadolibre/webhooks\` *(con App Check en modo permisivo)*.

Al recibir la notificación del cambio de estado, el backend actualiza la reserva en Firestore de forma atómica y publica un evento Pub/Sub de tipo \`payment.created\` o \`payment.approved\` para que el conductor sea despachado "Al Tiro" de forma asíncrona.`
  },

  // Grupo: EVENTOS & WEBHOOKS
  {
    id: "webhooks",
    titulo: "Webhooks en Tiempo Real",
    categoria: "desarrolladores",
    orden: 22,
    contenidoMarkdown: `## 📡 Eventos & Webhooks en Tiempo Real

Recibe notificaciones automáticas en tu servidor cuando ocurran cambios de estado o movimiento GPS de tus pedidos.

Configura tu URL de Webhooks desde el panel de administración. Cada notificación enviará un evento HTTP POST con la siguiente estructura JSON:

\`\`\`json
{
  "event": "order.status_changed",
  "order_id": "vz_ord_998123",
  "ecommerce_order_id": 1042,
  "booking_id": "vz_bk_98127341",
  "reference": "#1042",
  "status": "delivered",
  "timestamp": "2026-08-21T04:45:00.000Z",
  "driver": {
    "name": "Carlos Mamani",
    "phone": "+56911223344",
    "image": "https://storage.veez.cl/drivers/carlos.jpg",
    "vehicle": "Motocicleta Honda Cargo (AB-1234)"
  },
  "tracking_url": "https://veez.cl/track/vz_bk_98127341"
}
\`\`\``
  },

  // --- COMERCIOS & TIENDAS ---
  {
    id: "merchant-overview",
    titulo: "Visión General Comercios",
    categoria: "comercios",
    orden: 1,
    contenidoMarkdown: `## 🏪 Guía para Comercios & Tiendas

Guía operativa para comercios, restaurantes y tiendas e-commerce que automatizan sus despachos express con VeezDelivery.

### 🌟 Funcionalidades del Panel Comercio (app.veez.cl)
- **Despacho On-Demand / "Al Tiro"**: Asignación instantánea de choferes urbanos en minutos.
- **Ajuste de Tiempo de Preparación**: Configura cuántos minutos requiere tu almacén o cocina antes de que el repartidor retire.
- **Gestión de Sucursales**: Añade múltiples locales de origen con direcciones y contactos independientes.`
  },
  {
    id: "merchant-prep-time",
    titulo: "Tiempo de Preparación",
    categoria: "comercios",
    orden: 2,
    contenidoMarkdown: `## ⏱️ Configuración del Tiempo de Preparación
Evita demoras en cocina o almacén definiendo los minutos estimados de empaque.

> **Configuración Global vs Por Pedido:**  
> En tu Panel de Comercio (**Ajustes > Logística**) puedes fijar el tiempo global por defecto (ej. \`15 minutos\`). Al crear despachos vía API o Plugins, este valor puede personalizarse por pedido.`
  },
  {
    id: "merchant-shipping-rates",
    titulo: "Tarifas & Cobertura Urbana",
    categoria: "comercios",
    orden: 3,
    contenidoMarkdown: `## 💰 Tarifas Dinámicas & Zonas de Cobertura
VeezDelivery calcula automáticamente las tarifas basándose en la distancia exacta por carretera (GPS) y el volumen/peso de la carga.

- **Cobertura Urbana**: Región Metropolitana y principales ciudades de Chile.
- **Simulador de Tarifas**: Cotiza entregas directamente desde el Dashboard o mediante integración API.`
  },
  {
    id: "merchant-returns",
    titulo: "Gestión de Devoluciones",
    categoria: "comercios",
    orden: 4,
    contenidoMarkdown: `## 🔄 Devoluciones Inteligentes
Si una entrega no puede completarse, el repartidor iniciará el retorno de la mercadería hacia la tienda comercial de origen previa notificación.`
  },
  {
    id: "merchant-mercadopago",
    titulo: "Pago Automático con Mercado Pago",
    categoria: "comercios",
    orden: 5,
    contenidoMarkdown: `## 💳 Vinculación de Tarjetas & Pago Automático con Mercado Pago

Automatiza el pago de tus despachos urbanos vinculando de forma segura tu tarjeta de crédito o débito a través de **Mercado Pago**.

### 🌟 Beneficios de Pago Automático (Card-on-File)
- **Despachos Inmediatos**: Sin necesidad de recargar saldo manualmente ni realizar transferencias por cada pedido.
- **Seguridad Garantizada (PCI-DSS)**: VeezDelivery **no** almacena tus datos bancarios ni números de tarjeta en sus servidores. Toda la tokenización y almacenamiento de datos sensibles se realiza directamente en los servidores de Mercado Pago de forma encriptada.
- **Soporte de Tarjetas**: Compatible con cualquier tarjeta de crédito o débito Visa, Mastercard, American Express o Diners Club emitida en Chile (incluyendo tarjetas prepago como MACH, Tenpo o CuentaRUT).

---

### 📲 Cómo Vincular tu Tarjeta (Paso a Paso)

1. **Ingresa a tu Panel de Comercio**:
   Dirígete a tu Panel de Administración de Veez en [app.veez.cl](https://app.veez.cl/) e inicia sesión con tus credenciales de comercio.

2. **Accede a la Sección de Facturación**:
   En la barra lateral izquierda, selecciona la opción **Ajustes** y haz clic en la pestaña **Facturación & Métodos de Pago**.

3. **Agregar Nuevo Medio de Pago**:
   - Presiona el botón **Vincular Tarjeta con Mercado Pago**.
   - Se desplegará el formulario seguro y oficial de Mercado Pago de manera incrustada.
   - Ingresa los datos de tu tarjeta (Número de tarjeta, Fecha de vencimiento, Nombre del titular, RUT del titular y Código de Seguridad CVC).

4. **Validación y Registro**:
   - Al guardar, Mercado Pago generará un token seguro de un solo uso que nuestro backend recibirá para crear tu perfil de cliente en Mercado Pago.
   - El sistema puede realizar una pequeña verificación reversible de $1 CLP (un peso chileno) para corroborar la validez de la tarjeta.

5. **Definir Tarjeta Predeterminada**:
   - Si tienes múltiples tarjetas registradas, marca tu tarjeta de preferencia como **Predeterminada (Default)**.
   - El sistema cobrará de forma automatizada y programática a este método de pago por cada despacho creado en tu cuenta.

---

### ⚙️ Cómo Funciona el Cobro Automatizado

Cuando creas una solicitud de despacho en Veez (a través del Panel, tu plugin de WooCommerce/Shopify, o nuestra API REST v1):

1. **Creación del Despacho**: Se procesa el pedido y se calcula la tarifa dinámica exacta basándose en el GPS y volumen de la carga.
2. **Cargo Automático**: El backend seguro de Veez realiza un cargo programático directo a tu tarjeta predeterminada guardada en Mercado Pago utilizando una **llave de idempotencia única (UUID)** para evitar cobros duplicados en caso de inestabilidad de red.
3. **Ledger y Transacciones**:
   - Una vez aprobado el cargo por la entidad bancaria, la transacción se registra bajo la pestaña de **Historial de Transacciones** en tu panel con su respectivo identificador único de pago.
   - El despacho cambia automáticamente al estado **Confirmado** para que un conductor cercano sea asignado "Al Tiro".`
  },
  {
    id: "merchant-faq",
    titulo: "Preguntas Frecuentes Comercios",
    categoria: "comercios",
    orden: 6,
    contenidoMarkdown: `## ❓ Preguntas Frecuentes Comercios

#### ¿Cómo habilito el cobro automático a mi cuenta?
Inscribe tu medio de pago o tarjeta de crédito corporativa en el **Panel Comercio > Facturación**.

#### ¿Qué sucede si el pedido se entrega con retraso?
El sistema monitorea los ETAs en tiempo real y recalcula la ruta. En caso de imprevistos severos, el soporte te notificará proactivamente.`
  },

  // --- CLIENTES FINALES & DESTINATARIOS ---
  {
    id: "client-overview",
    titulo: "Visión General Destinatarios",
    categoria: "clientes",
    orden: 1,
    contenidoMarkdown: `## 👤 Guía para Clientes Finales & Destinatarios

Portal de consulta y seguimiento en vivo para compradores que reciben paquetes despachados por VeezDelivery.

### 📱 Seguimiento de tus Compras
- **Link de Rastreo SMS / Email**: Al enviarse tu pedido, recibes un enlace único para seguir al repartidor en el mapa.
- **Estimación de Arribo (ETA)**: Conoce el tiempo exacto en minutos hasta la llegada a tu domicilio.`
  },
  {
    id: "client-tracking",
    titulo: "Rastreo GPS en Tiempo Real",
    categoria: "clientes",
    orden: 2,
    contenidoMarkdown: `## 📍 Rastreo GPS en Mapa Interactivo
Abre el link de rastreo recibido para ver la posición satelital en vivo del chofer, vehículo asignado y nombre del conductor.`
  },
  {
    id: "client-delivery-verification",
    titulo: "Confirmación de Entrega (PIN)",
    categoria: "clientes",
    orden: 3,
    contenidoMarkdown: `## 🔑 Confirmación de Entrega Segura (Código PIN)
En despachos de alto valor, el repartidor te solicitará un código PIN de 4 dígitos enviado a tu teléfono para certificar la recepción del paquete.`
  },
  {
    id: "client-faq",
    titulo: "Preguntas Frecuentes Clientes",
    categoria: "clientes",
    orden: 4,
    contenidoMarkdown: `## ❓ Preguntas Frecuentes Clientes

#### ¿Puedo cambiar la dirección de entrega mientras el pedido va en camino?
Contacta inmediatamente al comercio emisor o al repartidor mediante el botón de llamada en la página de rastreo.

#### ¿Qué hago si no estoy en casa al momento de la llegada?
Puedes autorizar en conserjería o pedirle a un vecino que reciba el paquete proporcionándole el código PIN de confirmación.`
  },

  // --- REPARTIDORES ---
  {
    id: "driver-overview",
    titulo: "Visión General Conductores",
    categoria: "repartidores",
    orden: 1,
    contenidoMarkdown: `## 🚚 Guía para Repartidores & Conductores

Instrucciones operativas para la recolección, navegación GPS y entrega con confirmación por código PIN.

### 📍 Navegación Inteligente & Rutas
Optimización de rutas con Waze/Google Maps para evitar congestión vehicular y acelerar el tiempo de entrega.`
  },
  {
    id: "driver-eta",
    titulo: "Tiempos de Espera & Ventanas ETA",
    categoria: "repartidores",
    orden: 2,
    contenidoMarkdown: `## ⏱️ Tiempos de Espera & Ventanas ETA

> **Tiempo de Preparación en Pantalla:**  
> La App Móvil de Repartidor muestra la hora estimada en que el pedido estará empacado para evitar esperas innecesarias en el comercio.`
  },
  {
    id: "driver-faq",
    titulo: "Preguntas Frecuentes (FAQ)",
    categoria: "repartidores",
    orden: 3,
    contenidoMarkdown: `## ❓ Preguntas Frecuentes (FAQ)

#### ¿Qué hago si el cliente no responde al llegar a la dirección?
Utiliza el botón de llamada en la App. Transcurridos 10 minutos de espera, la App habilitará la opción de iniciar retorno a la tienda.`
  }
];
